// client/generic-field-formcontroller.ts
import { getTypeString } from "@adornis/baseql/baseqlTypes.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/datetime/x-date-picker-dropdown.js";
import "@adornis/file-utils/client/x-file-input.js";
import "@adornis/fonts/fonts.js";
import "@adornis/forms/x-checkbox.js";
import { FormController } from "@adornis/forms/x-form-controller.js";
import "@adornis/forms/x-input.js";
import "@adornis/popover/x-dropdown-multiselection.js";
import "@adornis/popover/x-dropdown-selection.js";
import "@adornis/tiptap/client/x-tiptap.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { until } from "lit/directives/until.js";
import "./generated-form.js";
var isTypedMapped = (value) => "typeName" in value;
var isFieldMapped = (value) => "fieldName" in value;
var enumBlockedKeys = ["_class", "_isBaseQLEnum"];
function filterEnumBlockedKeys(orginalEnum) {
  return Object.keys(orginalEnum).filter((key) => !enumBlockedKeys.includes(key));
}
function getMetaForField(entity, pathToField) {
  let thisMetaInfo = entity.fields[pathToField[0]];
  for (const key of pathToField.slice(1)) {
    if (!isNaN(+key))
      continue;
    thisMetaInfo = thisMetaInfo?.type()?.fields[key];
  }
  if (!thisMetaInfo)
    throw new Error(`No Meta found for field ${pathToField.join(",")}`);
  return thisMetaInfo;
}
var GenericFieldFormController = class extends FormController {
  constructor() {
    super(...arguments);
    this.t = new TranslationController(this.host);
  }
  _renderEnumDropDownSelectionField(keyArray, fieldOptions, originalEnum) {
    return xComponents(html`
      <x-dropdown-selection
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        info=${ifDefined(fieldOptions.info)}
        .selectables=${filterEnumBlockedKeys(originalEnum)}
        .renderString=${(entry) => {
      return entry ? this.t.translate("enums", entry) : entry;
    }}
        ${this.field(
      ...keyArray
    )}
      ></x-dropdown-selection>
    `);
  }
  _renderEnumDropDownMultiSelectionField(keyArray, fieldOptions, originalEnum) {
    return xComponents(html`
      <x-dropdown-multiselection
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        info=${ifDefined(fieldOptions.info)}
        .selectables=${filterEnumBlockedKeys(originalEnum)}
        .renderString=${(entry) => {
      return entry ? this.t.translate("enums", entry) : entry;
    }}
        ${this.field(
      ...keyArray
    )}
      ></x-dropdown-multiselection>
    `);
  }
  _renderEnum(field, keyArray, fieldOptions, typeClass) {
    const enumName = typeClass._class;
    const originalEnum = baseqlMetaData.enums[enumName]?.enum ?? {};
    if (!enumName)
      throw new Error(
        `Enum name is missing Attribute "field._class". default only baselEnums are supported. Maybe need a extend of generic-field-controller!`
      );
    if (baseqlMetaData.enums[enumName]?.dynamic)
      fieldOptions.humanReadable = fieldOptions.placeholder ?? this.t.translate("field", enumName);
    switch (field.arrayDepth()) {
      case 0:
        return this._renderEnumDropDownSelectionField(keyArray, fieldOptions, originalEnum);
      case 1:
        return this._renderEnumDropDownMultiSelectionField(keyArray, fieldOptions, originalEnum);
      default:
        return html`
          <x-text tone="error">
            Array depth above 1 is not supported by generic-field-formcontroller (field: ${field.name} // depth:
            ${field.arrayDepth()})
          </x-text>
        `;
    }
  }
  _renderCheckboxField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-checkbox
        ?plain=${fieldOptions.plainField}
        info=${ifDefined(fieldOptions.info)}
        label=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-checkbox>
    `);
  }
  _renderIntegerField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-input
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        step=${1}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
        type="number"
      ></x-input>
    `);
  }
  _renderFloatField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-input
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
        type="number"
      ></x-input>
    `);
  }
  _renderDateField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-input
        type="date"
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        info=${ifDefined(fieldOptions.info)}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-input>
    `);
  }
  _renderHtmlField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-tiptap
        mode="html"
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-tiptap>
    `);
  }
  _renderMarkdownField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-tiptap
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        mode="markdown"
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-tiptap>
    `);
  }
  _renderFileField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-file-input
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-file-input>
    `);
  }
  _renderStringField(keyArray, fieldOptions) {
    return xComponents(html`
      <x-input
        ?plain=${fieldOptions.plainField}
        placeholder=${until(
      fieldOptions.dynamicNonOptionalAsterisk.then((asterisk) => `${fieldOptions.humanReadable}${asterisk}`),
      fieldOptions.humanReadable
    )}
        placeholder-mode=${fieldOptions.placeholderMode}
        info=${ifDefined(fieldOptions.info)}
        ${// @ts-expect-error accept string array here
    this.field(...keyArray)}
      ></x-input>
    `);
  }
  _fieldFunctions() {
    return [
      {
        typeName: "boolean",
        render: (field, keys, fOpt, typeClass) => this._renderCheckboxField(keys, fOpt)
      },
      {
        typeName: "int",
        render: (field, keys, fOpt, typeClass) => this._renderIntegerField(keys, fOpt)
      },
      {
        typeName: "float",
        render: (field, keys, fOpt, typeClass) => this._renderFloatField(keys, fOpt)
      },
      {
        typeName: ["date", "datetime"],
        render: (field, keys, fOpt, typeClass) => this._renderDateField(keys, fOpt)
      },
      {
        typeName: "html",
        render: (field, keys, fOpt, typeClass) => this._renderHtmlField(keys, fOpt)
      },
      {
        typeName: "markdown",
        render: (field, keys, fOpt, typeClass) => this._renderMarkdownField(keys, fOpt)
      },
      {
        typeName: "sfile",
        render: (field, keys, fOpt, typeClass) => this._renderFileField(keys, fOpt)
      }
    ];
  }
  _getTargetFieldFunction(field, keyArray, fieldOptions, typeClass) {
    const typeName = fieldOptions.typeString.toLowerCase();
    const fieldName = field.name;
    for (const fieldFunction of this._fieldFunctions()) {
      let isTypeNameOk = true;
      if (!!typeName && isTypedMapped(fieldFunction))
        isTypeNameOk = fieldFunction.typeName == typeName || fieldFunction.typeName.includes(typeName);
      let isFieldNameOk = true;
      if (!!fieldName && isFieldMapped(fieldFunction))
        isFieldNameOk = fieldFunction.fieldName == field.name || fieldFunction.fieldName.includes(typeName);
      if (isTypeNameOk && isFieldNameOk) {
        return fieldFunction.render;
      }
    }
    return (field2, keys, fOpt, typeClass2) => this._renderStringField(keys, fOpt);
  }
  _renderField(field, keyArray, fieldOptions) {
    const typeClass = field.type();
    if (typeClass._isBaseQLEnum)
      return this._renderEnum(field, keyArray, fieldOptions, typeClass);
    return this._getTargetFieldFunction(field, keyArray, fieldOptions, typeClass)(
      field,
      keyArray,
      fieldOptions,
      typeClass
    );
  }
  /**
   * directive to mirror entity values to the form
   * @param keys key path to entity value you want to mirror to the form
   *
   * @example
   * ```html
   *  <x-input ${this.field("myNestedField", "myField")}></x-input>
   * ```
   */
  // eslint-disable-next-line complexity, max-lines-per-function
  genericField(keyArray, {
    placeholderMode = "inline-floating",
    plainField = false,
    placeholder,
    info
  } = {
    placeholderMode: "inline-floating",
    // TODO receive this from the generic form controller as soon as it supports it
    plainField: false
  }) {
    if (!this.document)
      throw new Error("Document not ready");
    const field = getMetaForField(this.document, keyArray);
    const typeString = getTypeString(field.type);
    const dynamicNonOptionalAsterisk = isFieldMandatory(field, this.document.constructor).then(
      (isMandatory) => isMandatory ? "*" : ""
    );
    const generatedPlaceholder = placeholder ?? this.t.translate(
      "field",
      ...keyArray.filter((entry) => isNaN(+entry))
    );
    let humanReadable = generatedPlaceholder;
    const fieldOptions = {
      placeholderMode,
      plainField,
      placeholder,
      info,
      humanReadable,
      dynamicNonOptionalAsterisk,
      typeString
    };
    return this._renderField(field, keyArray, fieldOptions);
  }
};
var isFieldMandatory = async (field, userClass) => {
  if (!userClass)
    return false;
  try {
    await userClass.prototype.validator.validateKey({ [field.name]: null }, field.name);
    return false;
  } catch {
    return true;
  }
};
export {
  GenericFieldFormController,
  getMetaForField,
  isFieldMandatory
};
